import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Popup from 'reactjs-popup'
import styled from 'particles/themes/styled'
import { useTranslation } from 'react-i18next'
import {
    REVIEW_MODAL_TYPE,
    makeNextStepSelector,
    selectCheckCanReviewSubmissionByStep,
    selectIsMultiSelectMode,
    selectSelectedCampaignSummary,
    selectSidebarStep,
    selectSidebarTask,
} from 'state/review'
import SingleRejectionForm from './SingleRejectionForm'
import MultiSelectOff from './MultiSelectOff'
import {} from 'components/utils'
import { dateIsInPast } from 'services/dates'
import { HandleMassSubmissionFunction } from '../types'
import RectangleButton from 'particles/Button/RectangleButton'
import Modal from 'particles/Modal'
import _ from 'lodash'
import MassApproveFormModal from './MassApproveFormModal'
import MassRejectAndEditModal from './MassRejectAndEditModal'
import StatusModal from './StatusModal'
import InfoModal from './InfoModal'
import Text from 'particles/Text'

interface MassModalWindowProps {
    type: string
    selectedNum?: number
    isOpenModal?: boolean
    setModalOpen?: any
    handleMassApprove?: HandleMassSubmissionFunction
    handleMassEditRequest?: HandleMassSubmissionFunction
    handleMassReject?: HandleMassSubmissionFunction
    handleMassApprovePending?: HandleMassSubmissionFunction
    handleMassRejectPending?: HandleMassSubmissionFunction
    handleEditTagChange?: any
    handleRejectTagChange?: any
}

const StyledPopup = styled(Popup)`
    &-content {
        border-radius: 8px;
        box-shadow:
            0px 11px 15px rgba(0, 0, 0, 0.2),
            0px 24px 38px rgba(0, 0, 0, 0.14),
            0px 9px 46px rgba(0, 0, 0, 0.12);
        .block {
            padding: 36px;
        }
    }
`

const MassModalWindow: React.FC<MassModalWindowProps> = ({
    type,
    selectedNum = 0,
    isOpenModal,
    setModalOpen,
    handleMassApprove,
    handleMassEditRequest,
    handleMassReject,
    handleMassApprovePending,
    handleMassRejectPending,
    handleRejectTagChange,
}) => {
    const [t] = useTranslation()
    const [open, setOpen] = useState<boolean>(false)
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const checkCanReviewSubmissionByStep = useSelector(selectCheckCanReviewSubmissionByStep)
    const selectedStep = useSelector(selectSidebarStep)
    const selectNextStep = useSelector(makeNextStepSelector)
    const sidebarTask = useSelector(selectSidebarTask)
    const selectedNextStep = selectNextStep(_, { step: selectedStep, task: sidebarTask })
    const campaignSummary = useSelector(selectSelectedCampaignSummary)

    const isButtonDisabled = useMemo(() => {
        if (campaignSummary && selectedStep) {
            // handle disable status for approve type button
            if (type === REVIEW_MODAL_TYPE.APPROVE) {
                // if there is next step & next step is end
                if (selectedNextStep && dateIsInPast(selectedNextStep.time_window.end, campaignSummary.country)) {
                    return true
                }

                // handle disable status for edit request button
            } else if (type === REVIEW_MODAL_TYPE.EDIT_REQUEST) {
                const isStepEnded = dateIsInPast(selectedStep.time_window.end, campaignSummary.country)
                if (isStepEnded) {
                    return true
                }
            }
        }

        if (!isMultiSelectMode) return true

        // check if no permission to review
        if (selectedStep && !checkCanReviewSubmissionByStep(selectedStep.type)) {
            return true
        }
        return false
    }, [isMultiSelectMode, selectedStep?.type])

    const btnType = (type: string) => {
        switch (type) {
            case REVIEW_MODAL_TYPE.APPROVE:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.approve')}
                        icon="checkSquare"
                        isDisabled={isButtonDisabled}
                        onClick={() => setOpen(true)}
                    />
                )
            case REVIEW_MODAL_TYPE.EDIT_REQUEST:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.editsRequested')}
                        icon="pencilLine"
                        isDisabled={isButtonDisabled}
                        onClick={() => setOpen(true)}
                    />
                )
            case REVIEW_MODAL_TYPE.REJECT:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.reject')}
                        icon="xCircle"
                        isDisabled={isButtonDisabled}
                        onClick={() => setOpen(true)}
                    />
                )
            case REVIEW_MODAL_TYPE.APPROVE_PENDING:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.acceptStatus')}
                        icon="checkSquare"
                        isDisabled={isButtonDisabled}
                        onClick={() => setOpen(true)}
                    />
                )
            case REVIEW_MODAL_TYPE.REJECT_PENDING:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.sendBack')}
                        icon="prohibit"
                        isDisabled={isButtonDisabled}
                        onClick={() => setOpen(true)}
                    />
                )
            case REVIEW_MODAL_TYPE.BLOCK_BY_DETAIL_ACTION:
                return (
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.approve')}
                        icon="checkSquare"
                        onClick={() => setOpen(true)}
                    />
                )
            default:
                return <></>
        }
    }

    const modalType = (type: string, selectedNum: number) => {
        switch (type) {
            case REVIEW_MODAL_TYPE.APPROVE:
                return (
                    <MassApproveFormModal
                        selectedNum={selectedNum}
                        handleMassApprove={handleMassApprove}
                        setOpen={setOpen}
                    />
                )
            case REVIEW_MODAL_TYPE.EDIT_REQUEST:
                return (
                    <MassRejectAndEditModal
                        type={type}
                        selectedNum={selectedNum}
                        handleAction={handleMassEditRequest}
                        setOpen={setOpen}
                    />
                )
            case REVIEW_MODAL_TYPE.REJECT:
                return (
                    <MassRejectAndEditModal
                        type={type}
                        selectedNum={selectedNum}
                        handleAction={handleMassReject}
                        setOpen={setOpen}
                    />
                )
            case REVIEW_MODAL_TYPE.APPROVE_PENDING:
                return (
                    <StatusModal
                        selectedNum={selectedNum}
                        handleMassAction={handleMassApprovePending}
                        setOpen={setOpen}
                        type={type}
                    />
                )
            case REVIEW_MODAL_TYPE.REJECT_PENDING:
                return (
                    <StatusModal
                        selectedNum={selectedNum}
                        handleMassAction={handleMassRejectPending}
                        setOpen={setOpen}
                        type={type}
                    />
                )
            case REVIEW_MODAL_TYPE.FILTER:
                return (
                    <MultiSelectOff
                        title={t('modalWindow.confirmationMessage.changeFilterTitle')}
                        description={t('modalWindow.confirmationMessage.wontSaveChangesSubtitle')}
                    />
                )
            case REVIEW_MODAL_TYPE.LEAVE:
                return (
                    <MultiSelectOff
                        title={t('modalWindow.confirmationMessage.leavePageTitle')}
                        description={t('modalWindow.confirmationMessage.wontSaveChangesSubtitle')}
                    />
                )
            case REVIEW_MODAL_TYPE.BLOCK_BY_DETAIL_ACTION:
                return (
                    <InfoModal
                        title={t('modalWindow.chooseActionTitle')}
                        subtext={t('modalWindow.cantApproveSubmissionMultiSelectSubtitle')}
                        buttonText={t('button.gotIt')}
                        onConfirm={() => setOpen(false)}
                        onClose={() => setOpen(false)}
                    >
                        <Text>{t('modalWindow.chooseActionDescription')}</Text>
                    </InfoModal>
                )
            case REVIEW_MODAL_TYPE.BATCH_BUDGET_USED_UP:
                return (
                    <Modal
                        title={t('warningMessage.campaignBudgetUsedUpTitle')}
                        subtext={t('warningMessage.cantViewSubmissionTitle')}
                        size="m"
                        icon="info"
                        primaryButtonProps={{
                            label: t('button.emailUs'),
                        }}
                        secondaryButtonProps={{
                            label: t('button.close'),
                        }}
                        onConfirm={e => {
                            setModalOpen(false)
                            window.location.href = 'mailto:info@partipost.com'
                            e.preventDefault()
                        }}
                        onClose={() => setModalOpen(false)}
                    >
                        <Text>{t('modalWindow.contactAccountManagerTopUpToSeeSubmissionDescription')}</Text>
                    </Modal>
                )
            case REVIEW_MODAL_TYPE.BATCH_ABOUT_WAITING_LIST:
                return (
                    <InfoModal
                        title={t('modalWindow.aboutWaitingListTitle')}
                        buttonText={t('button.gotIt')}
                        onConfirm={() => setModalOpen(false)}
                        onClose={() => setModalOpen(false)}
                    >
                        <>
                            <Text>{t('modalWindow.aboutWaitlistPartipostersPlacedInWaitlistDescription')}</Text>
                            <Text>{t('modalWindow.aboutWaitlistOnlyViewSubmissionsInWaitlistDescription')}</Text>
                            <Text>{t('modalWindow.aboutWaitlistContactAccountManagerDescription')}</Text>
                        </>
                    </InfoModal>
                )
            case REVIEW_MODAL_TYPE.BATCH_WHEN_WILL_SEE_WAITING_LIST:
                return (
                    <InfoModal
                        title={t('modalWindow.cantReviewSubmissionTitle')}
                        buttonText={t('button.gotIt')}
                        onConfirm={() => setModalOpen(false)}
                        onClose={() => setModalOpen(false)}
                    >
                        <>
                            <Text>
                                {t('modalWindow.whenToSeeWaitlistAccountManagerToAcceptSubmissionsDescription')}
                            </Text>
                            <Text>{t('modalWindow.aboutWaitlistContactAccountManagerDescription')}</Text>
                        </>
                    </InfoModal>
                )
            case REVIEW_MODAL_TYPE.SINGLE_REJECT:
            case REVIEW_MODAL_TYPE.BATCH_SINGLE_REJECT:
                return <SingleRejectionForm type={type} handleRejectTagChange={handleRejectTagChange} />
            case REVIEW_MODAL_TYPE.IN_WAITING_LIST:
                return (
                    <Modal
                        title={t('modalWindow.cantReviewSubmissionTitle')}
                        subtext={t('modalWindow.partiposterInWaitlistSubtitle')}
                        size="m"
                        icon="warning"
                        primaryButtonProps={{
                            label: t('button.gotIt'),
                        }}
                        hasSecondaryButton={false}
                        onConfirm={() => setModalOpen(false)}
                        onClose={() => setModalOpen(false)}
                    >
                        <Text>{t('modalWindow.unableToReviewSubmissionDescription')}</Text>
                    </Modal>
                )
            case REVIEW_MODAL_TYPE.REJECT_SUBMISSION_IN_WAITING_LIST:
                return (
                    <MultiSelectOff
                        title={t('modalWindow.rejectSubmissionTitle')}
                        description={t('modalWindow.submissionInWaitlistSubtitle')}
                        detail={t('modalWindow.rejectSubmissionInWaitlistDescription')}
                        confirmText={t('button.YesReject')}
                    />
                )
            default:
                return <></>
        }
    }
    switch (type) {
        case REVIEW_MODAL_TYPE.APPROVE:
        case REVIEW_MODAL_TYPE.EDIT_REQUEST:
        case REVIEW_MODAL_TYPE.REJECT:
        case REVIEW_MODAL_TYPE.APPROVE_PENDING:
        case REVIEW_MODAL_TYPE.REJECT_PENDING:
        case REVIEW_MODAL_TYPE.BLOCK_BY_DETAIL_ACTION:
            return (
                <>
                    {btnType(type)}
                    {open &&
                        (selectedNum > 0 || type == REVIEW_MODAL_TYPE.BLOCK_BY_DETAIL_ACTION) &&
                        modalType(type, selectedNum)}
                </>
            )
        case REVIEW_MODAL_TYPE.BATCH_BUDGET_USED_UP:
        case REVIEW_MODAL_TYPE.BATCH_ABOUT_WAITING_LIST:
        case REVIEW_MODAL_TYPE.BATCH_WHEN_WILL_SEE_WAITING_LIST:
        case REVIEW_MODAL_TYPE.IN_WAITING_LIST:
        case REVIEW_MODAL_TYPE.FILTER:
        case REVIEW_MODAL_TYPE.LEAVE:
        case REVIEW_MODAL_TYPE.REJECT_SUBMISSION_IN_WAITING_LIST:
            return <>{isOpenModal && modalType(type, selectedNum)}</>
        case REVIEW_MODAL_TYPE.SINGLE_REJECT:
        case REVIEW_MODAL_TYPE.BATCH_SINGLE_REJECT:
            return (
                <StyledPopup
                    open={isOpenModal}
                    closeOnDocumentClick={false}
                    onClose={() => setModalOpen(false)}
                    modal
                    closeOnEscape={false}
                >
                    <div className="block">{modalType(type, selectedNum)}</div>
                </StyledPopup>
            )
        default:
            return <></>
    }
}

export default MassModalWindow
